import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import "../scss/style.scss";
import './HistoricalDashboard.css';
import { useNavigate } from "react-router-dom";
import { FaDownload } from "react-icons/fa";
import Select, { components } from 'react-select';
import swal from 'sweetalert';
import config from '../config';
import { ENDPOINTS } from '../api/endpoints';
import { apiClient } from '../api/ticketsApi';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { CircleAlert } from 'lucide-react';
import { FiSearch } from 'react-icons/fi';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const parseLocalDate = (str) => { if (!str) return null; const [y,m,d] = str.split('-').map(Number); return new Date(y, m-1, d); };
const dateToStr = (date) => { if (!date) return ''; const y=date.getFullYear(),m=String(date.getMonth()+1).padStart(2,'0'),d=String(date.getDate()).padStart(2,'0'); return `${y}-${m}-${d}`; };
const environment = config.environment || process.env.REACT_APP_ENVIRONMENT;
const COLORS = ['#DC3545','#00C49F','#8884D8','#FF8042','#1281DA','#ee50b4','#27C966','#070e9f','#FF5722','#00BCD4','#FFC107','#3F51B5','#9C27B0'];

const Dashboard = () => {
  const navigate = useNavigate();
  const today = new Date();
  const currentYearStr = today.getFullYear().toString();
  const todayISO = today.toISOString().split('T')[0];

  const yearOptions = Array.from({ length: 2 }, (_, i) => ({
    value: (2026 - i).toString(),
    label: (2026 - i).toString()
  }));

  const airlineOptions = [
    { value: 'ALL', label: 'ALL' },
    { value: 'AC', label: 'AC' },
    { value: 'TK', label: 'TK' },
    { value: 'TS', label: 'TS' },
    { value: 'WS', label: 'WS' },
    { value: 'CM', label: 'CM' },
    { value: 'PD', label: 'PD' },
    { value: 'SQ', label: 'SQ' },
    { value: 'UA', label: 'UA' }
  ];

  const airlineDropdownOptions = [
    airlineOptions[0],
    ...[...airlineOptions.slice(1)].sort((a, b) => a.label.localeCompare(b.label))
  ];

  const defaultYears = [yearOptions[0]];
  const defaultAirlines = [{ value: 'ALL', label: 'ALL' }];
  const [selectedYears, setSelectedYears] = useState(defaultYears);
  const [selectedAirlines, setSelectedAirlines] = useState(defaultAirlines);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [downloading, setDownloading] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({
    years: [yearOptions[0]],
    airlines: [{ value: 'ALL', label: 'ALL' }],
    fromDate: '',
    toDate: ''
  });

  const [mainData, setMainData] = useState([]);
  const [totalPax, setTotalPax] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showDayWiseChart, setShowDayWiseChart] = useState(false);
  const [resetToDefault, setResetToDefault] = useState(false);
const [validationError, setValidationError] = useState("");

  const yearRange = useMemo(() => {
    if (!selectedYears.length) return { min: 2025, max: 2026, minDate: '2020-01-01', maxDate: todayISO };
    const years = selectedYears.map(y => parseInt(y.value));
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const minDate = `${minYear}-01-01`;
    const maxDate = maxYear.toString() === currentYearStr ? todayISO : `${maxYear}-12-31`;
    return { min: minYear, max: maxYear, minDate, maxDate };
  }, [selectedYears, todayISO, currentYearStr]);

  useEffect(() => { handleLoad(); }, []);

  useEffect(() => {
    if (resetToDefault) {
      handleLoad();
      setResetToDefault(false);
    }
  }, [resetToDefault]);

  useEffect(() => {
    const checkDate = (date, setter) => {
      if (!date) return;
      if (date < yearRange.minDate || date > yearRange.maxDate) setter('');
    };
    checkDate(fromDate, setFromDate);
    checkDate(toDate, setToDate);
  }, [selectedYears, yearRange]);

  const CheckboxOption = (props) => (
    <components.Option {...props}>
      <input type="checkbox" checked={props.isSelected} onChange={() => null} className='react-select-checkbox' style={{ marginRight: 8 }} />
      <span className="checkbox-label">{props.label}</span>
    </components.Option>
  );

  const handleYearChange = (values) => {
    if (!values || values.length === 0) {
      setSelectedYears(defaultYears); // restore default if cleared
    } else {
      setSelectedYears(values);
    }
  };

  const handleAirlineChange = (values, actionMeta) => {
    if (actionMeta.action === 'clear' || (values && values.length === 0)) {
      setSelectedAirlines(defaultAirlines);
      return;
    }
    if (actionMeta.option && actionMeta.option.value === 'ALL') {
      setSelectedAirlines([{ value: 'ALL', label: 'ALL' }]);
      return;
    }

    if (values && values.some(v => v.value === 'ALL') && values.length > 1) {
      const withoutAll = values.filter(v => v.value !== 'ALL');
      setSelectedAirlines(withoutAll);
      return;
    }
    setSelectedAirlines(values || []);
  };
  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const pdfContent = document.createElement('div');
      pdfContent.style.width = '1200px';
      pdfContent.style.padding = '20px';
      pdfContent.style.backgroundColor = '#fff';
      pdfContent.style.fontFamily = 'Arial, sans-serif';

      const header = document.createElement('h2');
      header.innerText = 'Historical Dashboard Report';
      header.style.textAlign = 'center';
      header.style.marginBottom = '20px';
      pdfContent.appendChild(header);

      const createTable = (title, headers, rows) => {
        const div = document.createElement('div');
        div.style.marginBottom = '30px';

        const tHeader = document.createElement('h3');
        tHeader.innerText = title;
        tHeader.style.marginBottom = '10px';
        div.appendChild(tHeader);

        const table = document.createElement('table');
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
        table.style.marginBottom = '10px';

        let html = '<thead><tr>';
        headers.forEach(h =>
          (html += `<th style="background-color:#1281DA; color:#fff; padding:8px; border:1px solid #ccc;">${h}</th>`)
        );
        html += '</tr></thead><tbody>';

        rows.forEach((row, idx) => {
          html += `<tr style="background-color:${idx % 2 === 0 ? '#f2f2f2' : '#ffffff'};">`;
          headers.forEach(h => (html += `<td style="padding:8px; border:1px solid #ccc;">${row[h] !== undefined ? row[h] : 0}</td>`));
          html += '</tr>';
        });

        html += '<tfoot><tr style="background-color:#f9f9f9; font-weight:bold; color:#ef5a31;">';
        headers.forEach((h, i) => {
          if (i === 0) html += `<td style="padding:8px; border:1px solid #ccc;">GRAND TOTAL</td>`;
          else {
            const total = rows.reduce((sum, row) => sum + (Number(row[h]) || 0), 0);
            html += `<td style="padding:8px; border:1px solid #ccc;">${total}</td>`;
          }
        });
        html += '</tr></tfoot>';

        table.innerHTML = html;
        div.appendChild(table);
        return div;
      };

      const yearHeaders = ['Year', ...appliedAirlinesToProcess.map(a => a.label)];
      const yearRows = computeYearlyData().map(row => {
        const r = { Year: row.year };
        appliedAirlinesToProcess.forEach(a => (r[a.label] = row[a.value] || 0));
        return r;
      });
      pdfContent.appendChild(createTable('Year-wise Airline Counts', yearHeaders, yearRows));

      const monthHeaders = ['Month', ...appliedAirlinesToProcess.map(a => a.label)];
      const monthRows = computeMonthYearChartData().map(row => {
        const r = { Month: row.month };
        appliedAirlinesToProcess.forEach(a => (r[a.label] = row[a.value] || 0));
        return r;
      });
      pdfContent.appendChild(createTable('Month-wise Airline Counts', monthHeaders, monthRows));

      const yearTotalHeaders = ['Year', 'Total Count'];
      const yearTotalRows = appliedFilters.years.map(y => ({
        Year: y.value,
        'Total Count': mainData.filter(d => d.date.getFullYear() === parseInt(y.value)).reduce((s, d) => s + d.pax, 0),
      }));
      pdfContent.appendChild(createTable('Year-wise Total Counts', yearTotalHeaders, yearTotalRows));

      const monthTotalHeaders = ['Year / Month', 'Total Count'];
      const monthTotalRows = computeMonthYearChartData().map(row => ({
        'Year / Month': row.month,
        'Total Count': Object.keys(row)
          .filter(k => k !== 'month')
          .reduce((s, k) => s + row[k], 0),
      }));
      pdfContent.appendChild(createTable('Month-wise Total Counts', monthTotalHeaders, monthTotalRows));

      const airlineHeaders = ['Airline', ...tableData.headers.map(h => h.label)];
      const airlineRows = tableData.rows.map(r => {
        const obj = { Airline: r.airline };
        tableData.headers.forEach(h => (obj[h.label] = r[h.key]));
        return obj;
      });
      pdfContent.appendChild(createTable('Airline-wise Month/Year Counts', airlineHeaders, airlineRows));

      if (showDayWiseChart) {
        const dayHeaders = ['Date', ...appliedAirlinesToProcess.map(a => a.label)];
        const dayRows = computeDayWiseChartData().map(row => {
          const r = { Date: row.date };
          appliedAirlinesToProcess.forEach(a => (r[a.label] = row[a.value] || 0));
          return r;
        });
        pdfContent.appendChild(createTable('Day-wise Airline Counts', dayHeaders, dayRows));
      }

      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      document.body.appendChild(pdfContent);

      const canvas = await html2canvas(pdfContent, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = pdfHeight;
      let position = 0;

      while (heightLeft > 0) {
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
        position -= pdf.internal.pageSize.getHeight();
        if (heightLeft > 0) pdf.addPage();
      }


      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text(`Page ${i} of ${pageCount}`, pdf.internal.pageSize.getWidth() - 20, pdf.internal.pageSize.getHeight() - 10);
      }

      pdf.save(`historical_dashboard_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.removeChild(pdfContent);
    } catch (err) {
      console.error('PDF generation failed', err);
      swal({ title: 'Error', text: 'Failed to generate PDF.', icon: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleLoad = async () => {
    setLoading(true);
    setMainData([]);

    const filtersToApply = {
      years: [...selectedYears],
      airlines: [...selectedAirlines],
      fromDate,
      toDate
    };

    try {
      const params = new URLSearchParams();
      filtersToApply.years.forEach(y => params.append('years', y.value));
      const isAllSelected = filtersToApply.airlines.some(a => a.value === 'ALL');

      if (isAllSelected) {
        airlineOptions.filter(opt => opt.value !== 'ALL').forEach(a => params.append('airlines', a.value));
      } else {
        filtersToApply.airlines.forEach(a => params.append('airlines', a.value));
      }
      params.append('environment', environment);
      if (filtersToApply.fromDate) params.append('fromDate', filtersToApply.fromDate);
      if (filtersToApply.toDate) params.append('toDate', filtersToApply.toDate);

      const response = await apiClient.get(`/api${ENDPOINTS.GET_HISTORICAL_SUMMARY}?${params.toString()}`);

      const data = response.data.map(d => ({ date: new Date(d.date), airline: d.airline, pax: d.count }));

      setMainData(data);
      setAppliedFilters(filtersToApply);
      setTotalPax(data.reduce((sum, item) => sum + item.pax, 0));

      if (filtersToApply.fromDate && filtersToApply.toDate) {
        const diffDays = (new Date(filtersToApply.toDate) - new Date(filtersToApply.fromDate)) / (1000 * 60 * 60 * 24);
        setShowDayWiseChart(diffDays <= 90);
      } else {
        setShowDayWiseChart(false);
      }
    } catch (err) {
      swal({ title: "Error", text: "Failed to fetch data.", icon: "error" }).then(() => {
        document.querySelector(".swal-modal")?.classList.add("swal-error-modal");
      });
    } finally {
      setLoading(false);
    }
  };
  const handleClearFilters = () => {
    setSelectedYears([yearOptions[0]]);
    setSelectedAirlines([{ value: 'ALL', label: 'ALL' }]);
    setFromDate('');
    setToDate('');
    setResetToDefault(true);
  };
  const appliedAirlinesToProcess = useMemo(() => {
    return appliedFilters.airlines.some(a => a.value === 'ALL')
      ? airlineOptions.filter(opt => opt.value !== 'ALL')
      : appliedFilters.airlines;
  }, [appliedFilters.airlines]);

  const computeDayWiseChartData = () => {
    const map = {};
    mainData.forEach(d => {
      const dateStr = d.date.toISOString().split('T')[0];
      if (!map[dateStr]) map[dateStr] = { date: dateStr };
      map[dateStr][d.airline] = (map[dateStr][d.airline] || 0) + d.pax;
    });
    return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
  };
  const computeYearlyData = () => {
    const map = {};
    mainData.forEach(item => {
      const year = item.date.getFullYear();
      if (!map[year]) map[year] = {};
      map[year][item.airline] = (map[year][item.airline] || 0) + item.pax;
    });
    return Object.entries(map).map(([year, airlines]) => ({ year, ...airlines }));
  };
  const computeMonthYearChartData = () => {
    if (!mainData.length) return [];
    const yearsSorted = appliedFilters.years.map(y => parseInt(y.value)).sort();
    const data = [];
    yearsSorted.forEach(year => {
      for (let m = 0; m < 12; m++) {
        const monthName = new Date(2000, m, 1).toLocaleString('default', { month: 'short' });
        const entry = { month: `${year}-${monthName}` };
        appliedAirlinesToProcess.forEach(a => {
          entry[a.value] = mainData
            .filter(d => d.airline === a.value && d.date.getFullYear() === year && d.date.getMonth() === m)
            .reduce((sum, d) => sum + d.pax, 0);
        });
        if (appliedAirlinesToProcess.some(a => entry[a.value] > 0)) data.push(entry);
      }
    });
    return data;
  };
  const tableData = useMemo(() => {
    if (!mainData.length) return { headers: [], rows: [] };
    const yearsSorted = appliedFilters.years.map(y => parseInt(y.value)).sort();
    const headers = [];
    yearsSorted.forEach(year => {
      for (let m = 0; m < 12; m++) {
        const monthName = new Date(2000, m, 1).toLocaleString('default', { month: 'short' });
        if (mainData.some(d => d.date.getFullYear() === year && d.date.getMonth() === m)) {
          headers.push({ year, month: monthName, key: `${year}-${monthName}`, label: `${monthName} ${year}` });
        }
      }
    });
    const rows = appliedAirlinesToProcess.map(a => {
      const row = { airline: a.label };
      headers.forEach(h => {
        row[h.key] = mainData
          .filter(d => d.airline === a.value && d.date.getFullYear() === h.year && d.date.toLocaleString('default', { month: 'short' }) === h.month)
          .reduce((sum, d) => sum + d.pax, 0);
      });
      return row;
    });
    return { headers, rows };
  }, [mainData, appliedFilters, appliedAirlinesToProcess]);

  return (
    <div className="dashboard-container historical-data">
      <div className="filter-form top-bar">
        <div className={"filter-section " + (loading || mainData.length === 0 ? 'full-width' : '')}>
          <div className='form-row w-full d-flex'>
            <div className="field-group form-group form-group-year">
              <label>Year</label>
              <Select
                isMulti
                options={yearOptions}
                value={selectedYears}
                onChange={handleYearChange}
                components={{ Option: CheckboxOption }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false} classNamePrefix="react-select" />
            </div>

            <div className="field-group form-group form-group-airline">
              <label>Airlines</label>
              <Select
                isMulti
                options={airlineDropdownOptions}
                value={selectedAirlines}
                onChange={handleAirlineChange}
                components={{ Option: CheckboxOption }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                classNamePrefix="react-select"
              />
            </div>
            {validationError && (
              <div className="validation-overlay">
                <div className="validation-modal warning-modal">
                  <div className="validation-icon icon"><CircleAlert /></div>
                  <div className='validation-content'>
                    <h4 className="validation-title title">Validation Error</h4>
                    <p className="validation-text description">{validationError}</p>
                  </div>
                  <button className="btn validation-button" onClick={() => setValidationError("")}>OK</button>
                </div>
              </div>
            )}
            <div className="field-group form-group form-group-date">
              <label>From Date</label>
              <DatePicker
                selected={parseLocalDate(fromDate)}
                onChange={(date) => {
                  const val = dateToStr(date);
                  setFromDate(val);
                  if (toDate && new Date(toDate) < new Date(val)) setToDate('');
                }}
                maxDate={new Date()}
                filterDate={(date) => !selectedYears.length || selectedYears.some(y => parseInt(y.value) === date.getFullYear())}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select from date"
                className="form-control date-input"
                wrapperClassName="hd-picker-wrap"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                popperPlacement="bottom-start"
                popperClassName="li-datepicker-popper"
                popperProps={{ strategy: 'fixed' }}
                autoComplete="off"
              />
            </div>
            <div className="field-group form-group form-group-date">
              <label>To Date {fromDate && <span style={{ color: "red", fontWeight: "bold" }}>*</span>}</label>
              <DatePicker
                selected={parseLocalDate(toDate)}
                onChange={(date) => {
                  if (!fromDate) { setValidationError("Please select 'From Date' first."); return; }
                  setToDate(dateToStr(date));
                }}
                minDate={parseLocalDate(fromDate) || undefined}
                maxDate={new Date()}
                filterDate={(date) => !selectedYears.length || selectedYears.some(y => parseInt(y.value) === date.getFullYear())}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select to date"
                className="form-control date-input"
                wrapperClassName="hd-picker-wrap"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                popperPlacement="bottom-start"
                popperClassName="li-datepicker-popper"
                popperProps={{ strategy: 'fixed' }}
                autoComplete="off"
              />
            </div>
          </div>
          <div className="button-group-container" style={{ display: 'flex', gap: 20 }}>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (fromDate && !toDate) {
                  swal({
                    title: "Validation Error",
                    text: "To Date is mandatory when 'From Date' is selected.",
                    icon: "warning",
                  });
                  return;
                }

                if (toDate && !fromDate) {
                  swal({
                    title: "Validation Error",
                    text: "Please select 'From Date' first.",
                    icon: "warning",
                  });
                  return;
                }

                handleLoad();
              }}
              disabled={loading}
            >
              {'Apply'}
            </button>
            <button className="btn btn-reset" onClick={handleClearFilters}>Reset</button>
            <button className="btn btn-download-link" onClick={handleDownloadPDF} disabled={downloading} >
              {downloading ? 'Downloading...' : 'Download'}
            </button>
          </div>
        </div>
        {!loading && mainData.length > 0 && (
          <div className="kpi-cardd total-count-box align-items-start d-flex">
            <span className='icon ticket-icon'><svg xmlns="http://w3.org" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.5 17h1.227a2 2 0 0 0 1.345-.52L18 12" /><path d="m12 13.5 3.794.506" /><path d="m3.173 8.18 11-5a2 2 0 0 1 2.647.993L18.56 8" /><rect x="2" y="8" width="20" height="13" rx="2" /></svg></span>
            <div><h5>TOTAL COUNT</h5><h2 className="stat-value">{totalPax.toLocaleString()}</h2></div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loader-wrap" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', marginTop: '40px', gap: '15px' }}>
          <div className="spinner"></div>
          <p className="text-center" style={{ fontWeight: '600', color: '#070e9f', fontSize: '16px', margin: 0 }}>Loading Historical data...</p>
        </div>
      )}

      {!loading && mainData.length === 0 && (
        <div className="no-data-wrap">
          <FiSearch size={36} color="#9da2ba" />
          <p className="no-data-title">No Data Found</p>
          <p className="no-data-sub">Selected filter data is not available.</p>
        </div>
      )}

      {!loading && mainData.length > 0 && (
        <>
          <div className='card-row'>
            {showDayWiseChart && (
              <div className="data-card data-card-day w-full">
                <div className="card-header">DAY-WISE RESULTS</div>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer><BarChart data={computeDayWiseChartData()}><CartesianGrid vertical={false} stroke="#eee" /><XAxis dataKey="date" tickFormatter={d => new Date(d).toLocaleDateString('en-GB')} angle={-45} textAnchor="end" height={70} /><YAxis /><Tooltip shared={true} /><Legend verticalAlign="top" height={36} />{appliedAirlinesToProcess.map((a, idx) => <Bar key={a.value} dataKey={a.value} stackId="a" fill={COLORS[idx % COLORS.length]} name={a.label} />)}</BarChart></ResponsiveContainer>
                </div>
              </div>
            )}
            <div className="data-card data-card-year">
              <div className="card-header">YEAR-WISE RESULTS</div>
              <div style={{ height: 350 }}>
                <ResponsiveContainer><BarChart data={computeYearlyData()} barCategoryGap="10%">
                  <CartesianGrid vertical={false} stroke="#eee" /><XAxis dataKey="year" /><YAxis /><Tooltip shared={true} /><Legend verticalAlign="top" height={36} />{appliedAirlinesToProcess.map((a, idx) => <Bar key={a.value} dataKey={a.value} stackId="a" fill={COLORS[idx % COLORS.length]} name={a.label} barSize={100} />
                  )}</BarChart></ResponsiveContainer>
              </div>
            </div>
            <div className="data-card data-card-month">
              <div className="card-header">MONTH-WISE RESULTS</div>
              <div style={{ height: 400 }}>
                <ResponsiveContainer><BarChart data={computeMonthYearChartData()}><CartesianGrid vertical={false} stroke="#eee" /><XAxis dataKey="month" angle={-45} textAnchor="end" height={70} /><YAxis />
                  <Tooltip shared={true} contentStyle={{ width: 'fit-content', padding: '5px 10px' }} formatter={(value) => value.toLocaleString()} /> <Legend verticalAlign="top" height={36} />
                  {appliedAirlinesToProcess.map((a, idx) => (
                    // Change Bar tag inside map to:
                    <Bar key={a.value} dataKey={a.value} stackId="a" fill={COLORS[idx % COLORS.length]} name={a.label} barSize={50} />

                  ))}</BarChart></ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className='card-row card-row-count'> {/* historical-data-card-row*/}
            <div className="data-card data-card-year-count">
              <div className="card-header">YEAR-WISE COUNT</div>
              <div className='table-horizontal-wrap'>
                <div className='table-header-box'>
                  <table className='table'>
                    <tr>
                      <th>Year</th>
                    </tr>
                    <tr>
                      <th>Total Count</th>
                    </tr> 
                  </table>
                </div>

                <div className='table-body-box'>
                  <table className='table'>
                    <tr>
                      {appliedFilters.years.map(y => (
                        <th>{y.value}</th>
                      ))}
                      <th className='total-th'>Grand Total</th>
                    </tr>
                    <tr>
                      {appliedFilters.years.map(y => (
                        <td>{mainData.filter(d => d.date.getFullYear() === parseInt(y.value)).reduce((s, d) => s + d.pax, 0).toLocaleString()}</td>
                      ))}
                      <td className='total-value-td' colSpan={appliedFilters.years.length}>
                        {totalPax.toLocaleString()}
                      </td>
                    </tr> 
                  </table>
                </div>
                {/* <div className='table-footer-box'> </div> */}
              </div>

              {/* <table className="revenue-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 15px' }}>Year</th>
                    <th style={{ textAlign: 'left', padding: '12px 15px' }}>Total Count</th>
                  </tr>
                </thead>
                <tbody>
                  {appliedFilters.years.map(y => (
                    <tr key={y.value}>
                      <td style={{ textAlign: 'left', padding: '10px 15px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>{y.value}</td>
                      <td style={{ textAlign: 'left', padding: '10px 15px', borderBottom: '1px solid #eee' }}>{mainData.filter(d => d.date.getFullYear() === parseInt(y.value)).reduce((s, d) => s + d.pax, 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#f9f9f9', borderTop: '2px solid #ccc' }}>
                    <td style={{ textAlign: 'left', padding: '12px 15px', fontWeight: 'bold', color: '#ef5a31' }}>GRAND TOTAL</td>
                    <td style={{ textAlign: 'left', padding: '12px 15px', fontWeight: 'bold', color: '#ef5a31' }}>{totalPax.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table> */}

            </div>

            <div className="data-card data-card-month-count">
              <div className="card-header">MONTH-WISE TOTAL COUNT</div>
              <div className='table-horizontal-wrap'>
                <div className='table-header-box'>
                  <table className='table'>
                    <tr>
                      <th>Year / Month</th>
                    </tr>
                    <tr>
                      <th>Total Count</th>
                    </tr>
                    
                  </table>
                </div>

                <div className='table-body-box'>
                  <table className='table'>
                    <tr>
                      {computeMonthYearChartData().map((row) => (
                        <th>{row.month}</th>
                      ))}
                      <th className='total-th'>Grand Total</th>
                    </tr>
                    <tr>
                      {computeMonthYearChartData().map((row, idx) => (
                        <td>{Object.keys(row).filter(k => k !== 'month').reduce((s, k) => s + (row[k] || 0), 0).toLocaleString()}</td>
                      ))}
                      <td className='total-value-td' colSpan={computeMonthYearChartData().length}>
                        {totalPax.toLocaleString()}
                      </td>
                    </tr> 
                  </table>
                </div>
                {/* <div className='table-footer-box'> </div> */}
              </div>
              {/* <table className="revenue-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 20px' }}>Year / Month</th>
                    <th style={{ textAlign: 'left', padding: '12px 20px' }}>Total Count</th>
                  </tr>
                </thead>
                <tbody>
                  {computeMonthYearChartData().map((row) => (
                    <tr key={row.month}>
                      <td style={{ textAlign: 'left', padding: '10px 20px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>{row.month}</td>
                      <td style={{ textAlign: 'left', padding: '10px 20px', borderBottom: '1px solid #eee' }}>{Object.keys(row).filter(k => k !== 'month').reduce((s, k) => s + (row[k] || 0), 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#f9f9f9', borderTop: '2px solid #ccc' }}>
                    <td style={{ textAlign: 'left', padding: '12px 20px', fontWeight: 'bold', color: '#ef5a31' }}>GRAND TOTAL</td>
                    <td style={{ textAlign: 'left', padding: '12px 20px', fontWeight: 'bold', color: '#ef5a31' }}>{totalPax.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table> */}
            </div>

            <div className="data-card  data-card-airline-count">
              <div className="card-header">AIRLINE-WISE MONTHLY COUNT</div>

              {tableData.headers.length < 8 ? (
                <div className='table-horizontal-wrap'>
                  <div className='table-header-box'>
                    <table className='table'>
                      <thead>
                        <tr><th>Airline</th></tr>
                        {tableData.headers.map(h => (
                          <tr key={h.key}>
                            <th>{h.label}</th>
                          </tr>
                        ))}
                        {/* <tr>
                        <th className='total-th'>Grand Total</th>
                      </tr> */}
                      </thead>
                    </table>
                  </div>

                  <div className='table-body-box'>
                    <table className='table'>
                      <tbody>
                        {/* Airline names row */}
                        <tr>
                          {tableData.rows.map(r => (
                            <th key={r.airline}>{r.airline}</th>
                          ))}

                          <th className='total-th'>Grand Total</th>
                        </tr>

                        {/* Data rows for each header */}
                        {tableData.headers.map(h => (
                          <tr key={h.key}>
                            {tableData.rows.map(r => (
                              <td key={r.airline + h.key}>
                                {(r[h.key] || 0).toLocaleString()}
                              </td>
                            ))}
                            <td className='total-value-td'>
                              {tableData.rows.reduce((s, r) => s + (r[h.key] || 0), 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className='airline-data-wrap'>
                  <table className="revenue-table table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '12px 15px', width: '150px' }}>Airline</th>
                        {tableData.headers.map(h => <th key={h.key} style={{ textAlign: 'left', padding: '12px 15px' }}>{h.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.rows.map(r => (
                        <tr key={r.airline}>
                          <td style={{ textAlign: 'left', padding: '10px 15px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>{r.airline}</td>
                          {tableData.headers.map(h => <td key={h.key} style={{ textAlign: 'left', padding: '10px 15px', borderBottom: '1px solid #eee' }}>{(r[h.key] || 0).toLocaleString()}</td>)}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: '#f9f9f9', borderTop: '2px solid #ccc' }}>
                        <td className='total-th'>Grand Total</td>
                        {tableData.headers.map(h => <td key={h.key} style={{ textAlign: 'left', padding: '12px 15px', fontWeight: 'bold', color: '#ef5a31' }}>{tableData.rows.reduce((s, r) => s + (r[h.key] || 0), 0).toLocaleString()}</td>)}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
